import * as React from 'react'
import {
  useGetTagMappingJobDetailQuery,
  useListTagMappingTypesQuery,
  usePreviewTagMappingMutation,
  useSuggestTagMappingMutation,
  type TagMappingGrammarPair,
  type TagMappingGrammarPreview,
  type TagMappingLinkExclude,
  type TagMappingGrammarToken,
  type TagMappingJob,
  type TagMappingPreviewResult,
  type TagMappingPreviewRow,
  type TagMappingTagBreakdown,
  type TagMappingTagSegment
} from '@axilon-apps/domains-ontology-ui-state'
import { Link2, Loader2, Search, Sparkles, Unlink } from 'lucide-react'
import { JobLogs } from '../../../components/JobLogs/JobLogs'
import { Button } from '../../../components/ui/button'
import { Label } from '../../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table'
import { Textarea } from '../../../components/ui/textarea'
import { cn } from '../../../lib/utils'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import {
  addManualLinks,
  excludeStoredLink,
  isManualLink,
  isStoredLinkExcluded,
  keepLinkedGrammars,
  MANUAL_LINK_GRAMMAR_ID,
  parseStoredGrammars,
  parseStoredManualLinks,
  removeManualLink,
  serializeStoredGrammars,
  serializeStoredManualLinks
} from './tagMappingGrammars'
import { TagMappingLinkDialog, type TagLinkSource } from './TagMappingLinkDialog'
import {
  clampPage,
  decodeSegment,
  defaultLookupTab,
  defaultSegmentIndex,
  humanizeSlotName,
  isPunctuationLiteral,
  LOOKUP_PAGE_SIZE,
  lookupTagInPreview,
  namesWithNoGrammar,
  pageCount,
  pageSlice,
  samplesForSide,
  type LookupResultTab,
  type MappingSide
} from './tagMappingPreviewView'

const PREVIEW_DEBOUNCE_MS = 350
const TagMappingLabelsContext = React.createContext({ a: 'A', b: 'B' })

function useTagMappingLabels() {
  return React.useContext(TagMappingLabelsContext)
}

function isAbortError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const record = error as Record<string, unknown>
  if (record.name === 'AbortError') return true
  const text = [record.error, record.message, record.status]
    .filter((value) => typeof value === 'string')
    .join(' ')
  return /abort/i.test(text)
}

function previewErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const record = error as Record<string, unknown>
    if (typeof record.data === 'string' && record.data.trim()) return record.data
    if (record.data && typeof record.data === 'object') {
      const data = record.data as Record<string, unknown>
      if (typeof data.error === 'string' && data.error.trim()) return data.error
      if (typeof data.message === 'string' && data.message.trim()) return data.message
    }
    if (typeof record.error === 'string' && record.error.trim()) return record.error
    if (typeof record.message === 'string' && record.message.trim()) return record.message
  }
  return 'Could not preview this tag mapping.'
}

export function TagMappingStudio({
  grammarsJson,
  manualLinksJson,
  suggestJobId,
  mappingTypeId,
  siteId,
  additionalContext,
  onGrammarsChange,
  onManualLinksChange,
  onSuggestJobIdChange,
  onMappingTypeIdChange,
  onAdditionalContextChange
}: {
  grammarsJson: string
  manualLinksJson?: string
  suggestJobId?: string
  mappingTypeId?: string
  siteId?: string
  additionalContext?: string
  onGrammarsChange: (grammarsJson: string) => void
  onManualLinksChange: (manualLinksJson: string) => void
  onSuggestJobIdChange: (jobId: string) => void
  onMappingTypeIdChange?: (mappingTypeId: string) => void
  onAdditionalContextChange: (value: string) => void
}) {
  const grammars = parseStoredGrammars(grammarsJson)
  const manuals = parseStoredManualLinks(manualLinksJson)
  const [linkSource, setLinkSource] = React.useState<TagLinkSource | null>(null)
  const mappingTypesQuery = useListTagMappingTypesQuery()
  const mappingTypes = React.useMemo(() => mappingTypesQuery.data ?? [], [mappingTypesQuery.data])
  const effectiveMappingTypeId = mappingTypeId ?? mappingTypes[0]?.id
  const selectedMappingType = mappingTypes.find((type) => type.id === effectiveMappingTypeId)
  const aLabel = selectedMappingType?.sides.a.label ?? 'Side A'
  const bLabel = selectedMappingType?.sides.b.label ?? 'Side B'
  const [suggestTagMapping, suggestState] = useSuggestTagMappingMutation()
  const [previewTagMapping, previewState] = usePreviewTagMappingMutation()
  const [preview, setPreview] = React.useState<TagMappingPreviewResult | undefined>()
  const suggestJobQuery = useGetTagMappingJobDetailQuery(suggestJobId ?? '', {
    skip: !suggestJobId
  })
  const suggestJob = suggestJobQuery.data
  const suggestRunning =
    suggestState.isLoading ||
    suggestJob?.state === 'processing' ||
    suggestJob?.state === 'queued' ||
    suggestJob?.state === 'uploading'
  const suggestFinished = suggestJob?.state === 'done'
  const hasMappingResults = grammars.length > 0 || manuals.length > 0 || suggestFinished
  const showTagResults = hasMappingResults && !suggestRunning

  React.useEffect(() => {
    const first = mappingTypes[0]
    if (!mappingTypeId && first) onMappingTypeIdChange?.(first.id)
  }, [mappingTypeId, mappingTypes, onMappingTypeIdChange])

  const appliedSuggestJob = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (!suggestJob || suggestJob.state !== 'done') return
    if (appliedSuggestJob.current === suggestJob.id) return
    const parsed = grammarsFromSuggestJob(suggestJob)
    if (parsed.length === 0) return
    appliedSuggestJob.current = suggestJob.id
    onGrammarsChange(serializeStoredGrammars(parsed))
    const jobPreview = suggestJob.result?.preview
    if (jobPreview && typeof jobPreview === 'object') {
      const preview = jobPreview as TagMappingPreviewResult
      const keptIds = new Set(parsed.map((pair) => pair.id))
      setPreview({
        ...preview,
        grammars: (preview.grammars ?? []).filter((slice) => keptIds.has(slice.id))
      })
    }
  }, [suggestJob, onGrammarsChange])

  const logScrollerRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (!suggestRunning) return
    const scroller = logScrollerRef.current
    if (!scroller) return
    scroller.scrollTop = scroller.scrollHeight
  }, [suggestRunning, suggestJob?.logCount])

  React.useEffect(() => {
    if (suggestRunning) setLinkSource(null)
  }, [suggestRunning])

  React.useEffect(() => {
    if (!siteId || !effectiveMappingTypeId || suggestRunning) return
    const pairs = parseStoredGrammars(grammarsJson)
    const manualLinks = parseStoredManualLinks(manualLinksJson)
    if (pairs.length === 0 && manualLinks.length === 0 && !suggestFinished) return
    let aborted = false
    let request: ReturnType<typeof previewTagMapping> | undefined
    const handle = window.setTimeout(() => {
      if (aborted) return
      request = previewTagMapping({
        mappingTypeId: effectiveMappingTypeId,
        siteId,
        ...(pairs.length > 0 ? { grammars: pairs } : {}),
        ...(manualLinks.length > 0 ? { manualLinks } : {})
      })
      void request
        .unwrap()
        .then((result) => {
          if (aborted) return
          setPreview(result)
          if (pairs.length === 0) return
          const kept = keepLinkedGrammars(pairs, result.grammars)
          if (serializeStoredGrammars(kept) === serializeStoredGrammars(pairs)) return
          onGrammarsChange(serializeStoredGrammars(kept))
        })
        .catch((error: unknown) => {
          if (!aborted && !isAbortError(error)) setPreview(undefined)
        })
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      aborted = true
      window.clearTimeout(handle)
      request?.abort()
    }
  }, [
    grammarsJson,
    manualLinksJson,
    effectiveMappingTypeId,
    siteId,
    suggestRunning,
    suggestFinished,
    previewTagMapping,
    onGrammarsChange
  ])

  const suggest = () => {
    if (!siteId || !effectiveMappingTypeId) return
    const trimmedContext = additionalContext?.trim()
    void suggestTagMapping({
      mappingTypeId: effectiveMappingTypeId,
      siteId,
      ...(trimmedContext ? { additionalContext: trimmedContext } : {})
    })
      .unwrap()
      .then((result) => {
        onSuggestJobIdChange(result.jobId)
      })
  }

  const dropGrammar = (id: string) => {
    onGrammarsChange(serializeStoredGrammars(grammars.filter((grammar) => grammar.id !== id)))
  }

  const unlink = (link: { aName: string; bName: string; grammarId?: string }) => {
    if (link.grammarId === MANUAL_LINK_GRAMMAR_ID || isManualLink(manuals, link)) {
      onManualLinksChange(serializeStoredManualLinks(removeManualLink(manuals, link)))
      return
    }
    onGrammarsChange(serializeStoredGrammars(excludeStoredLink(grammars, link)))
  }

  const confirmManualLinks = (counterparts: string[]) => {
    if (!linkSource) return
    const added: TagMappingLinkExclude[] = counterparts.map((name) =>
      linkSource.side === 'a'
        ? { aName: linkSource.name, bName: name }
        : { aName: name, bName: linkSource.name }
    )
    onManualLinksChange(serializeStoredManualLinks(addManualLinks(manuals, added)))
    setLinkSource(null)
  }

  const suggestError =
    suggestState.isError && !isAbortError(suggestState.error)
      ? previewErrorMessage(suggestState.error)
      : null
  const previewError =
    previewState.isError && !isAbortError(previewState.error)
      ? previewErrorMessage(previewState.error)
      : null

  const changeMappingType = (nextMappingTypeId: string) => {
    if (nextMappingTypeId === effectiveMappingTypeId) return
    onMappingTypeIdChange?.(nextMappingTypeId)
    onGrammarsChange('')
    onManualLinksChange('')
    onSuggestJobIdChange('')
    setPreview(undefined)
    setLinkSource(null)
  }

  return (
    <TagMappingLabelsContext.Provider value={{ a: aLabel, b: bLabel }}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-11 font-medium text-ink-2">Mapping type</Label>
          <Select
            value={effectiveMappingTypeId}
            onValueChange={changeMappingType}
            disabled={mappingTypesQuery.isLoading || suggestRunning}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a mapping type" />
            </SelectTrigger>
            <SelectContent>
              {mappingTypes.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {mappingTypesQuery.isError ? (
            <p role="alert" className="text-xs text-st-down">
              Could not load available mapping types.
            </p>
          ) : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="tagmap-additional-context" className="text-11 font-medium text-ink-2">
            Additional context (optional)
          </Label>
          <Textarea
            id="tagmap-additional-context"
            value={additionalContext ?? ''}
            placeholder="Naming conventions, unit codes, known synonym pairs, prefixes to ignore…"
            rows={3}
            maxLength={4000}
            disabled={suggestRunning}
            onChange={(e) => onAdditionalContextChange(e.target.value)}
          />
          <p className="text-10 leading-snug text-ink-3">
            Passed to the grammar-suggest agent alongside site tag samples.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="max-w-[52ch] text-xs leading-snug text-ink-2">
            Suggest grammars, then browse, unlink, or manually link {aLabel} and {bLabel}.
          </p>
          <Button
            type="button"
            size="sm"
            onClick={suggest}
            disabled={!siteId || !effectiveMappingTypeId || suggestRunning}
          >
            {suggestRunning ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Sparkles className="size-3.5" />
            )}
            {grammars.length > 0 ? 'Suggest again' : 'Suggest grammars'}
          </Button>
        </div>
        {!siteId ? (
          <p role="status" className="text-xs text-st-warn">
            Tag mapping needs an existing site with {aLabel} and {bLabel} already ingested.
          </p>
        ) : null}
        {suggestError ? (
          <p role="alert" className="text-xs text-st-down">
            {suggestError}
          </p>
        ) : null}
        {previewError ? (
          <p role="alert" className="text-xs text-st-down">
            {previewError}
          </p>
        ) : null}
        {suggestJob?.state === 'error' && suggestJob.error ? (
          <p role="alert" className="text-xs text-st-down">
            {suggestJob.error}
          </p>
        ) : null}
        {suggestJobId && (suggestRunning || (suggestJob?.logCount ?? 0) > 0) ? (
          <details
            className="overflow-hidden rounded-md border border-line-2 bg-bg-elev"
            open={suggestRunning ? true : undefined}
          >
            <summary className="cursor-pointer px-2.5 py-1.5 text-xs font-medium text-ink">
              Agent thinking
            </summary>
            <div
              ref={logScrollerRef}
              className="max-h-[28rem] overflow-auto border-t border-line-2 p-2"
            >
              <JobLogs
                jobId={suggestJobId}
                emptyHint="Waiting for the agent to start thinking…"
                defaultExpanded
              />
            </div>
          </details>
        ) : null}
        {suggestRunning ? (
          <div role="status" className="flex items-center gap-2 text-xs text-ink-3">
            <Loader2 className="size-3.5 shrink-0 animate-spin" />
            Exploring tag names and writing how they relate…
          </div>
        ) : null}
        {showTagResults
          ? preview?.issues.map((issue) => (
              <p
                key={issue.message}
                role={issue.severity === 'error' ? 'alert' : 'status'}
                className={
                  issue.severity === 'error' ? 'text-xs text-st-down' : 'text-xs text-st-warn'
                }
              >
                {issue.message}
              </p>
            ))
          : null}
        {showTagResults && preview ? (
          <>
            <TagLookup
              preview={preview}
              grammars={grammars}
              onUnlink={unlink}
              onLink={setLinkSource}
            />
            {(preview.grammars ?? [])
              .filter((grammar) => grammars.some((pair) => pair.id === grammar.id))
              .map((grammar) => (
                <GrammarCard
                  key={grammar.id}
                  grammar={grammar}
                  links={linksForGrammar(preview, grammar)}
                  grammars={grammars}
                  onRemove={() => dropGrammar(grammar.id)}
                  onUnlink={unlink}
                  onLink={setLinkSource}
                />
              ))}
          </>
        ) : null}
        {showTagResults && !preview && previewState.isLoading ? (
          <div role="status" className="flex items-center gap-2 text-xs text-ink-3">
            <Loader2 className="size-3.5 shrink-0 animate-spin" />
            Matching proposed grammars…
          </div>
        ) : null}
        {showTagResults && preview ? (
          <UnmatchedSummary preview={preview} onLink={setLinkSource} />
        ) : null}
        {showTagResults ? (
          <ManualLinksSection
            links={manualRows(preview, manuals)}
            onUnlink={unlink}
            onLink={setLinkSource}
          />
        ) : null}
        {showTagResults ? (
          <TagMappingLinkDialog
            source={linkSource}
            preview={preview ?? {}}
            sideLabels={{ a: aLabel, b: bLabel }}
            alreadyLinked={alreadyLinkedCounterparts(preview, manuals, linkSource)}
            onClose={() => setLinkSource(null)}
            onConfirm={confirmManualLinks}
          />
        ) : null}
        {grammars.length === 0 && manuals.length === 0 && !suggestRunning ? (
          <p className="text-xs text-ink-3">
            Click Suggest grammars to start. You can drop a proposed grammar if it does not apply.
          </p>
        ) : null}
      </div>
    </TagMappingLabelsContext.Provider>
  )
}

function GrammarCard({
  grammar,
  links,
  grammars,
  onRemove,
  onUnlink,
  onLink
}: {
  grammar: TagMappingGrammarPreview
  links: readonly TagMappingPreviewRow[]
  grammars: readonly TagMappingGrammarPair[]
  onRemove?: () => void
  onUnlink?: (link: UnlinkTarget) => void
  onLink?: (source: TagLinkSource) => void
}) {
  const [page, setPage] = React.useState(0)
  const visible = visibleLinks(links, grammars)
  return (
    <section className="flex flex-col gap-3 rounded-md border border-line-2 bg-bg-sunk p-3">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-13 font-medium text-ink">{grammar.label ?? grammar.id}</h3>
          {grammar.rationale ? (
            <p className="mt-0.5 text-11 leading-snug text-ink-3">{grammar.rationale}</p>
          ) : null}
        </div>
        {onRemove ? (
          <Button type="button" size="sm" variant="ghost" onClick={onRemove}>
            Remove
          </Button>
        ) : null}
      </header>
      <CoverageStrip grammar={grammar} />
      <TagDecoder
        joinKeys={grammar.joinKeys}
        aSamples={grammar.aSamples ?? []}
        bSamples={grammar.bSamples ?? []}
        aTokens={grammar.aTokens}
        bTokens={grammar.bTokens}
      />
      {visible.length > 0 ? (
        <PagedLinkTable
          title={`Links (${visible.length})`}
          links={visible}
          page={page}
          onPageChange={setPage}
          onUnlink={onUnlink}
          onLink={onLink}
          pagerLabel={`${grammar.label ?? grammar.id} links`}
        />
      ) : (
        <p className="text-xs text-ink-3">No links from this grammar yet.</p>
      )}
    </section>
  )
}

function CoverageStrip({ grammar }: { grammar: TagMappingGrammarPreview }) {
  const labels = useTagMappingLabels()
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <CoverageStat
        label={labels.a}
        value={`${grammar.a.matched}/${grammar.a.total}`}
        ratio={ratio(grammar.a.matched, grammar.a.total)}
      />
      <CoverageStat
        label={labels.b}
        value={`${grammar.b.matched}/${grammar.b.total}`}
        ratio={ratio(grammar.b.matched, grammar.b.total)}
      />
      <CoverageStat
        label="Links"
        value={String(grammar.matchCount)}
        hint={`${grammar.uniqueCount} unique`}
        ratio={grammar.matchCount > 0 ? 1 : 0}
      />
      <div className="rounded-md border border-line-2 bg-bg-elev px-2.5 py-2">
        <p className="text-10 font-medium tracking-[0.06em] text-ink-3 uppercase">Join</p>
        <p className="mt-1 font-mono text-11 leading-snug text-ink">
          {grammar.joinKeys.map(humanizeSlotName).join(' · ') || '—'}
        </p>
      </div>
    </div>
  )
}

function CoverageStat({
  label,
  value,
  ratio: coverage,
  hint
}: {
  label: string
  value: string
  ratio: number
  hint?: string
}) {
  const empty = coverage === 0
  return (
    <div className="rounded-md border border-line-2 bg-bg-elev px-2.5 py-2">
      <p className="text-10 font-medium tracking-[0.06em] text-ink-3 uppercase">{label}</p>
      <p className={cn('mt-0.5 font-mono text-13', empty ? 'text-st-warn' : 'text-ink')}>{value}</p>
      {hint ? <p className="text-10 text-ink-3">{hint}</p> : null}
      <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-line-2">
        <div
          className={cn('h-full', empty ? 'bg-st-warn' : 'bg-brand')}
          style={{ width: `${Math.round(coverage * 100)}%` }}
        />
      </div>
    </div>
  )
}

function TagDecoder({
  joinKeys,
  aSamples,
  bSamples,
  aTokens,
  bTokens
}: {
  joinKeys: readonly string[]
  aSamples: readonly TagMappingTagBreakdown[]
  bSamples: readonly TagMappingTagBreakdown[]
  aTokens: readonly TagMappingGrammarToken[]
  bTokens: readonly TagMappingGrammarToken[]
}) {
  const labels = useTagMappingLabels()
  const [side, setSide] = React.useState<MappingSide>('a')
  const samples = samplesForSide(side, aSamples, bSamples)
  const tokens = side === 'a' ? aTokens : bTokens
  const [sampleIndex, setSampleIndex] = React.useState(0)
  const sample = samples[Math.min(sampleIndex, Math.max(samples.length - 1, 0))]
  const segments = sample?.segments ?? []
  const [segmentIndex, setSegmentIndex] = React.useState(() =>
    defaultSegmentIndex(segments, joinKeys)
  )

  React.useEffect(() => {
    setSampleIndex(0)
    const next = samplesForSide(side, aSamples, bSamples)
    setSegmentIndex(defaultSegmentIndex(next[0]?.segments ?? [], joinKeys))
  }, [side, joinKeys, aSamples, bSamples])

  React.useEffect(() => {
    setSegmentIndex(defaultSegmentIndex(sample?.segments ?? [], joinKeys))
  }, [sample, joinKeys])

  const selected = segments[segmentIndex]
  const token = tokens[segmentIndex]
  const decoded = selected ? decodeSegment(selected, token, joinKeys) : undefined

  return (
    <div className="flex flex-col gap-2.5 rounded-md border border-line-2 bg-bg-elev p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <SideToggle side={side} onChange={setSide} />
        {samples.length > 0 ? (
          <div className="flex min-w-0 flex-1 gap-1 overflow-x-auto">
            {samples.map((entry, index) => {
              const active = index === sampleIndex
              return (
                <button
                  key={entry.name}
                  type="button"
                  onClick={() => setSampleIndex(index)}
                  className={cn(
                    'shrink-0 rounded-md border px-2 py-1 font-mono text-11',
                    active
                      ? 'border-brand bg-brand text-white'
                      : 'border-line-2 bg-bg-sunk text-ink hover:border-brand/40'
                  )}
                >
                  {entry.name}
                </button>
              )
            })}
          </div>
        ) : (
          <p className="text-11 text-ink-3">No {labels[side]} names matched this grammar.</p>
        )}
      </div>
      {sample ? (
        <>
          <SegmentStrip
            segments={segments}
            joinKeys={joinKeys}
            selectedIndex={segmentIndex}
            onSelect={setSegmentIndex}
          />
          {decoded ? <SegmentDecodePane decode={decoded} /> : null}
        </>
      ) : null}
    </div>
  )
}

function SideToggle({
  side,
  onChange
}: {
  side: MappingSide
  onChange: (side: MappingSide) => void
}) {
  const labels = useTagMappingLabels()
  return (
    <div className="flex shrink-0 rounded-md border border-line-2 p-0.5">
      {(['a', 'b'] as const).map((value) => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          className={cn(
            'rounded px-2 py-1 text-10 font-medium tracking-[0.06em] uppercase',
            side === value ? 'bg-brand text-white' : 'text-ink-3 hover:text-ink'
          )}
        >
          {labels[value]}
        </button>
      ))}
    </div>
  )
}

function SegmentStrip({
  segments,
  joinKeys,
  selectedIndex,
  onSelect
}: {
  segments: readonly TagMappingTagSegment[]
  joinKeys: readonly string[]
  selectedIndex: number
  onSelect: (index: number) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-stretch gap-1">
        {segments.map((segment, index) => {
          if (isPunctuationLiteral(segment)) {
            return (
              <span
                key={`${segment.kind}:${index}`}
                className="self-center px-0.5 font-mono text-12 text-ink-3"
              >
                {segment.text}
              </span>
            )
          }
          const selected = index === selectedIndex
          const join =
            segment.kind === 'slot' && segment.name != null && joinKeys.includes(segment.name)
          return (
            <button
              key={`${segment.kind}:${index}:${segment.text}`}
              type="button"
              onClick={() => onSelect(index)}
              className={cn(
                'min-w-10 rounded-md border px-2.5 py-2 font-mono text-13 leading-none',
                selected
                  ? 'border-brand bg-brand text-white'
                  : join
                    ? 'border-brand/40 bg-brand-soft text-brand hover:border-brand'
                    : 'border-line-2 bg-bg-sunk text-ink hover:border-brand/40'
              )}
            >
              {segment.text}
            </button>
          )
        })}
      </div>
      <p className="text-10 font-medium tracking-[0.08em] text-ink-3 uppercase">
        Click a segment to decode it
      </p>
    </div>
  )
}

function SegmentDecodePane({ decode }: { decode: ReturnType<typeof decodeSegment> }) {
  return (
    <div className="rounded-md border border-line-2 bg-bg-sunk px-3 py-2.5">
      <p className="text-10 font-medium tracking-[0.08em] text-brand uppercase">{decode.title}</p>
      <p className="mt-0.5 text-13 font-medium text-ink">{decode.kindLabel}</p>
      <p className="mt-1 text-12 leading-snug text-ink-2">{decode.summary}</p>
      <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-11">
        <DecodeFact label="Captured" value={decode.captured} />
        {decode.canonical ? <DecodeFact label="Linked as" value={decode.canonical} /> : null}
        {decode.typeLabel ? <DecodeFact label="Type" value={decode.typeLabel} /> : null}
        {decode.aliases && decode.aliases.length > 0 ? (
          <DecodeFact label="Also matches" value={decode.aliases.join(', ')} />
        ) : null}
        {decode.regex ? <DecodeFact label="Pattern" value={decode.regex} /> : null}
      </dl>
    </div>
  )
}

function DecodeFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-10 tracking-[0.06em] text-ink-3 uppercase">{label}</dt>
      <dd className="font-mono text-ink">{value}</dd>
    </div>
  )
}

function TagLookup({
  preview,
  grammars,
  onUnlink,
  onLink
}: {
  preview: TagMappingPreviewResult
  grammars?: readonly TagMappingGrammarPair[]
  onUnlink?: (link: UnlinkTarget) => void
  onLink?: (source: TagLinkSource) => void
}) {
  const labels = useTagMappingLabels()
  const [query, setQuery] = React.useState('')
  const [tab, setTab] = React.useState<LookupResultTab>('links')
  const [page, setPage] = React.useState(0)
  const result = lookupTagInPreview(preview, query)
  const searching = query.trim().length > 0
  const matches = visibleLinks(
    searching
      ? result.matches
      : result.matches.filter((row) => row.grammarId !== MANUAL_LINK_GRAMMAR_ID),
    grammars ?? []
  )
  const tooShort = query.trim().length > 0 && query.trim().length < 2
  const empty =
    searching &&
    !tooShort &&
    matches.length === 0 &&
    result.unmatched.length === 0 &&
    result.unparsed.length === 0
  const visibleResult = { ...result, matches }
  const activeTab = lookupTabCount(tab, visibleResult) > 0 ? tab : defaultLookupTab(visibleResult)
  const showTabs =
    !tooShort &&
    !empty &&
    (matches.length > 0 || result.unmatched.length > 0 || result.unparsed.length > 0)
  const searchUnlink = searching && !tooShort && matches[0] && onUnlink ? matches[0] : undefined

  React.useEffect(() => {
    setPage(0)
  }, [query, tab])

  return (
    <section className="flex flex-col gap-2 rounded-md border border-line-2 bg-bg-elev p-2.5">
      <label className="flex flex-col gap-1.5">
        <span className="text-10 font-medium tracking-[0.06em] text-ink-3 uppercase">
          Look up a tag
        </span>
        <span className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-ink-3" />
          <input
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setPage(0)
            }}
            placeholder={`${labels.a} or ${labels.b} name`}
            className={cn(
              'h-8 w-full rounded-md border border-line-2 bg-bg-sunk pl-7 font-mono text-12 text-ink outline-none placeholder:font-sans placeholder:text-ink-3 focus:border-brand',
              searchUnlink ? 'pr-20' : 'pr-2'
            )}
          />
          {searchUnlink ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="absolute top-1/2 right-0.5 h-7 -translate-y-1/2 px-2"
              onClick={() => onUnlink?.(searchUnlink)}
              aria-label={`Unlink ${searchUnlink.aName} from ${searchUnlink.bName}`}
            >
              <Unlink className="size-3.5" />
              Unlink
            </Button>
          ) : null}
        </span>
      </label>
      {tooShort ? <p className="text-11 text-ink-3">Type at least two characters.</p> : null}
      {empty ? (
        <p role="status" className="text-11 text-ink-3">
          No tag in this preview contains “{result.query}”.
        </p>
      ) : null}
      {!searching && matches.length > 0 ? (
        <p className="text-11 text-ink-3">
          {matches.length} proposed link{matches.length === 1 ? '' : 's'}. Search to filter, or
          unlink a row.
        </p>
      ) : null}
      {showTabs ? (
        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            setTab(value as LookupResultTab)
            setPage(0)
          }}
          className="gap-2"
        >
          <TabsList variant="line" aria-label="Lookup results" className="h-7">
            <TabsTrigger value="links" className="px-2 text-11">
              Links ({matches.length})
            </TabsTrigger>
            <TabsTrigger value="unmatched" className="px-2 text-11">
              Parsed, no counterpart ({result.unmatched.length})
            </TabsTrigger>
            <TabsTrigger value="unparsed" className="px-2 text-11">
              No match ({result.unparsed.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="links" aria-label="Links">
            <PagedLinkTable
              links={matches}
              page={page}
              onPageChange={setPage}
              onUnlink={onUnlink}
              onLink={onLink}
              empty="No links for this query."
              pagerLabel="Lookup page"
            />
          </TabsContent>
          <TabsContent value="unmatched" aria-label="Parsed, no counterpart">
            <PagedNameTable
              names={result.unmatched}
              page={page}
              onPageChange={setPage}
              onLink={onLink}
              empty="No parsed names without a counterpart."
              pagerLabel="Lookup page"
            />
          </TabsContent>
          <TabsContent value="unparsed" aria-label="No match">
            <PagedNameTable
              names={result.unparsed}
              page={page}
              onPageChange={setPage}
              onLink={onLink}
              empty="Every matching name is in a grammar."
              pagerLabel="Lookup page"
            />
          </TabsContent>
        </Tabs>
      ) : null}
    </section>
  )
}

type UnlinkTarget = { aName: string; bName: string; grammarId?: string }

type LinkRow = {
  aName: string
  bName: string
  slots: Record<string, string>
  unique: boolean
  grammarId?: string
}

function linksForGrammar(
  preview: TagMappingPreviewResult,
  grammar: TagMappingGrammarPreview
): TagMappingPreviewRow[] {
  const fromPreview = (preview.matches ?? []).filter((row) => row.grammarId === grammar.id)
  return fromPreview.length > 0 ? fromPreview : (grammar.matches ?? [])
}

function visibleLinks<T extends UnlinkTarget>(
  links: readonly T[],
  grammars: readonly TagMappingGrammarPair[]
): T[] {
  if (grammars.length === 0) return [...links]
  return links.filter((row) => !isStoredLinkExcluded(grammars, row))
}

function lookupTabCount(
  tab: LookupResultTab,
  result: ReturnType<typeof lookupTagInPreview>
): number {
  if (tab === 'links') return result.matches.length
  if (tab === 'unmatched') return result.unmatched.length
  return result.unparsed.length
}

function joinLabel(row: {
  slots: Record<string, string>
  unique: boolean
  grammarId?: string
}): string {
  if (row.grammarId === MANUAL_LINK_GRAMMAR_ID) return 'Manual'
  const joined = Object.entries(row.slots)
    .map(([key, value]) => `${humanizeSlotName(key)} ${value}`)
    .join(' · ')
  if (!joined) return row.unique ? '' : 'review'
  return `${joined}${row.unique ? '' : ' · review'}`
}

function manualRows(
  preview: TagMappingPreviewResult | undefined,
  manuals: readonly TagMappingLinkExclude[]
): LinkRow[] {
  const fromPreview = preview?.manualMatches ?? []
  const seen = new Set(
    fromPreview.map((row) => `${row.aName.toUpperCase()}\0${row.bName.toUpperCase()}`)
  )
  const extras = manuals
    .filter((row) => !seen.has(`${row.aName.toUpperCase()}\0${row.bName.toUpperCase()}`))
    .map((row) => ({
      aName: row.aName,
      bName: row.bName,
      slots: {},
      unique: true,
      grammarId: MANUAL_LINK_GRAMMAR_ID
    }))
  return [
    ...fromPreview.map((row) => ({
      ...row,
      grammarId: row.grammarId ?? MANUAL_LINK_GRAMMAR_ID
    })),
    ...extras
  ]
}

function alreadyLinkedCounterparts(
  preview: TagMappingPreviewResult | undefined,
  manuals: readonly TagMappingLinkExclude[],
  source: TagLinkSource | null
): Set<string> {
  const names = new Set<string>()
  if (!source) return names
  const needle = source.name.trim().toUpperCase()
  const rows = [...(preview?.matches ?? []), ...(preview?.manualMatches ?? []), ...manuals]
  for (const row of rows) {
    if (source.side === 'a' && row.aName.trim().toUpperCase() === needle) {
      names.add(row.bName.trim().toUpperCase())
    }
    if (source.side === 'b' && row.bName.trim().toUpperCase() === needle) {
      names.add(row.aName.trim().toUpperCase())
    }
  }
  return names
}

function UnmatchedSummary({
  preview,
  onLink
}: {
  preview: TagMappingPreviewResult
  onLink?: (source: TagLinkSource) => void
}) {
  const labels = useTagMappingLabels()
  const rows = namesWithNoGrammar(preview)
  const [page, setPage] = React.useState(0)
  if (rows.length === 0) return null
  return (
    <section className="flex flex-col gap-2 rounded-md border border-line-2 bg-bg-elev p-2.5">
      <h3 className="text-xs font-medium text-ink">
        Match no grammar ({preview.a.unparsed} {labels.a} · {preview.b.unparsed} {labels.b})
      </h3>
      <PagedNameTable
        names={rows}
        page={page}
        onPageChange={setPage}
        onLink={onLink}
        pagerLabel="No-grammar page"
      />
    </section>
  )
}

function ManualLinksSection({
  links,
  onUnlink,
  onLink
}: {
  links: readonly LinkRow[]
  onUnlink?: (link: UnlinkTarget) => void
  onLink?: (source: TagLinkSource) => void
}) {
  const [page, setPage] = React.useState(0)
  return (
    <section className="flex flex-col gap-2 rounded-md border border-line-2 bg-bg-elev p-2.5">
      <h3 className="text-xs font-medium text-ink">Manually linked tags ({links.length})</h3>
      {links.length === 0 ? (
        <p className="text-11 text-ink-3">
          Link an unmatched or already-joined tag to add an explicit pair here.
        </p>
      ) : (
        <PagedLinkTable
          links={links}
          page={page}
          onPageChange={setPage}
          onUnlink={onUnlink}
          onLink={onLink}
          pagerLabel="Manual links page"
        />
      )}
    </section>
  )
}

export function TagMappingPreviewTables({
  preview,
  grammars,
  manuals,
  onUnlink,
  onLink,
  onRemoveGrammar,
  labels
}: {
  preview: TagMappingPreviewResult
  grammars?: readonly TagMappingGrammarPair[]
  manuals?: readonly TagMappingLinkExclude[]
  onUnlink?: (link: UnlinkTarget) => void
  onLink?: (source: TagLinkSource) => void
  onRemoveGrammar?: (grammarId: string) => void
  labels?: { a: string; b: string }
}) {
  const tables = (
    <div className="flex flex-col gap-2.5">
      {preview.issues.map((issue) => (
        <p
          key={issue.message}
          role={issue.severity === 'error' ? 'alert' : 'status'}
          className={issue.severity === 'error' ? 'text-xs text-st-down' : 'text-xs text-st-warn'}
        >
          {issue.message}
        </p>
      ))}
      <p className="text-11 text-ink-3">
        {preview.matchCount} link{preview.matchCount === 1 ? '' : 's'} ({preview.uniqueCount}{' '}
        unique, {preview.ambiguousCount} need review).
      </p>
      <TagLookup preview={preview} grammars={grammars} onUnlink={onUnlink} onLink={onLink} />
      {(preview.grammars ?? [])
        .filter((grammar) => (grammars ?? []).some((pair) => pair.id === grammar.id))
        .map((grammar) => (
          <GrammarCard
            key={grammar.id}
            grammar={grammar}
            links={linksForGrammar(preview, grammar)}
            grammars={grammars ?? []}
            onUnlink={onUnlink}
            onLink={onLink}
            {...(onRemoveGrammar ? { onRemove: () => onRemoveGrammar(grammar.id) } : {})}
          />
        ))}
      {preview.matchCount === 0 && (preview.matches?.length ?? 0) === 0 ? (
        <p className="text-xs text-ink-3">No matches yet.</p>
      ) : null}
      <UnmatchedSummary preview={preview} onLink={onLink} />
      <ManualLinksSection
        links={manualRows(preview, manuals ?? [])}
        onUnlink={onUnlink}
        onLink={onLink}
      />
    </div>
  )
  return labels ? (
    <TagMappingLabelsContext.Provider value={labels}>{tables}</TagMappingLabelsContext.Provider>
  ) : (
    tables
  )
}

function PagedLinkTable({
  title,
  links,
  page,
  onPageChange,
  onUnlink,
  onLink,
  empty,
  pagerLabel
}: {
  title?: string
  links: readonly LinkRow[]
  page: number
  onPageChange: (page: number) => void
  onUnlink?: (link: UnlinkTarget) => void
  onLink?: (source: TagLinkSource) => void
  empty?: string
  pagerLabel?: string
}) {
  const labels = useTagMappingLabels()
  if (links.length === 0) {
    return empty ? <p className="text-11 text-ink-3">{empty}</p> : null
  }
  const safePage = clampPage(page, links.length)
  const pages = pageCount(links.length)
  const rows = pageSlice(links, safePage)
  const showActions = Boolean(onUnlink || onLink)
  const headers = showActions ? [labels.a, labels.b, 'Join', ''] : [labels.a, labels.b, 'Join']
  return (
    <section className="overflow-hidden rounded-md border border-line-2 bg-bg-elev">
      {title ? (
        <header className="border-b border-line-2 px-2.5 py-1.5">
          <h3 className="text-xs font-medium text-ink">{title}</h3>
        </header>
      ) : null}
      <Table className="text-11">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {headers.map((header, index) => (
              <TableHead
                key={header || `action-${index}`}
                className={cn(
                  'h-7 px-2 font-mono text-ink-2',
                  header === '' ? 'w-[1%]' : undefined
                )}
              >
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`${row.aName}:${row.bName}:${row.grammarId ?? ''}:${index}`}>
              <TableCell className="px-2 py-1 font-mono">{row.aName}</TableCell>
              <TableCell className="px-2 py-1 font-mono">{row.bName}</TableCell>
              <TableCell className="px-2 py-1 font-mono">{joinLabel(row)}</TableCell>
              {showActions ? (
                <TableCell className="px-1 py-0.5">
                  <div className="flex items-center justify-end gap-0.5">
                    {onLink ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={() => onLink({ side: 'a', name: row.aName })}
                        aria-label={`Link ${row.aName}`}
                      >
                        <Link2 className="size-3.5" />
                        Link
                      </Button>
                    ) : null}
                    {onUnlink ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={() => onUnlink(row)}
                        aria-label={`Unlink ${row.aName} from ${row.bName}`}
                      >
                        <Unlink className="size-3.5" />
                        Unlink
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pages > 1 ? (
        <TablePager
          page={safePage}
          pageCount={pages}
          total={links.length}
          onPageChange={onPageChange}
          label={pagerLabel}
        />
      ) : null}
    </section>
  )
}

function PagedNameTable({
  names,
  page,
  onPageChange,
  onLink,
  empty,
  pagerLabel
}: {
  names: ReadonlyArray<{ side: MappingSide; name: string }>
  page: number
  onPageChange: (page: number) => void
  onLink?: (source: TagLinkSource) => void
  empty?: string
  pagerLabel?: string
}) {
  const labels = useTagMappingLabels()
  if (names.length === 0) {
    return empty ? <p className="text-11 text-ink-3">{empty}</p> : null
  }
  const safePage = clampPage(page, names.length)
  const pages = pageCount(names.length)
  const rows = pageSlice(names, safePage)
  const headers = onLink ? ['Side', 'Name', ''] : ['Side', 'Name']
  return (
    <section className="overflow-hidden rounded-md border border-line-2 bg-bg-elev">
      <Table className="text-11">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {headers.map((header, index) => (
              <TableHead
                key={header || `action-${index}`}
                className={cn(
                  'h-7 px-2 font-mono text-ink-2',
                  header === '' ? 'w-[1%]' : undefined
                )}
              >
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`${row.side}:${row.name}:${index}`}>
              <TableCell className="px-2 py-1 font-mono">{labels[row.side]}</TableCell>
              <TableCell className="px-2 py-1 font-mono">{row.name}</TableCell>
              {onLink ? (
                <TableCell className="px-1 py-0.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => onLink({ side: row.side, name: row.name })}
                    aria-label={`Link ${row.name}`}
                  >
                    <Link2 className="size-3.5" />
                    Link
                  </Button>
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pages > 1 ? (
        <TablePager
          page={safePage}
          pageCount={pages}
          total={names.length}
          onPageChange={onPageChange}
          label={pagerLabel}
        />
      ) : null}
    </section>
  )
}

function TablePager({
  page,
  pageCount: pages,
  total,
  onPageChange,
  label
}: {
  page: number
  pageCount: number
  total: number
  onPageChange: (page: number) => void
  label?: string
}) {
  const from = page * LOOKUP_PAGE_SIZE + 1
  const to = Math.min(total, from + LOOKUP_PAGE_SIZE - 1)
  return (
    <div
      role="navigation"
      aria-label={label ?? 'Table page'}
      className="flex items-center justify-between gap-2 border-t border-line-2 px-2.5 py-1.5"
    >
      <p className="text-10 text-ink-3">
        {from}–{to} of {total}
      </p>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={page <= 0}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <span className="px-1 text-10 text-ink-3">
          {page + 1}/{pages}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={page >= pages - 1}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

function ratio(matched: number, total: number): number {
  if (total <= 0) return 0
  return Math.min(1, matched / total)
}

function grammarsFromSuggestJob(job: TagMappingJob) {
  const raw = job.result?.grammars
  if (raw == null) return []
  try {
    const parsed = parseStoredGrammars(JSON.stringify(raw))
    const preview = job.result?.preview
    const slices =
      preview && typeof preview === 'object'
        ? (preview as TagMappingPreviewResult).grammars
        : undefined
    return keepLinkedGrammars(parsed, slices)
  } catch {
    return []
  }
}
